import type React from 'react';
import { useEffect } from 'react';

interface PageSEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schemaJsonLd?: Record<string, unknown>;
}

const PageSEO: React.FC<PageSEOProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schemaJsonLd,
}) => {
  useEffect(() => {
    const originalTitle = document.title;
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);

    if (keywords) {
      setMeta('keywords', keywords);
    }

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) {
      setMeta('og:url', canonicalUrl, true);
    }
    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }

    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    let scriptEl: HTMLScriptElement | null = null;
    if (schemaJsonLd) {
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.textContent = JSON.stringify(schemaJsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = originalTitle;
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schemaJsonLd]);

  return null;
};

export default PageSEO;